import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase/client';
import { User } from './types';
import AuthForm from './components/AuthForm';
import Dashboard from './components/Dashboard';
import TermsAndConditions from './components/TermsAndConditions';
import PrivacyPolicy from './components/PrivacyPolicy';
import FormGuidePage from './pages/FormGuidePage';
import AdditionalInfoPage from './pages/AdditionalInfoPage';
import SuccessPage from './pages/SuccessPage';
import CancelPage from './pages/CancelPage';
import { initializeUserTokens } from './services/tokenService';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { useAutoLogout } from './hooks/useAutoLogout';
import { ToastContainer } from 'react-toastify';
import { analyticsService } from './services/analyticsService';
import { useLanguage } from './contexts/LanguageContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { usePlatform } from './hooks/usePlatform';
import { usePWAInstall } from './hooks/usePWAInstall';
import InstallPWAPrompt from './components/InstallPWAPrompt';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { App as CapApp } from '@capacitor/app';
import 'react-toastify/dist/ReactToastify.css';
import { Truck } from 'lucide-react';

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showTerms, setShowTerms] = useState<boolean>(false);
  const [showPrivacy, setShowPrivacy] = useState<boolean>(false);
  const { t } = useLanguage();
  const { isNative, isIOS, isAndroid } = usePlatform();
  const { canInstall, isInstalled, wasDismissed, promptInstall, dismissPrompt } = usePWAInstall();
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);

  // Handle user logout
  const handleUserLoggedOut = () => {
    setUser(null);
  };

  // Bandera para prevenir múltiples ciclos de procesamiento de autenticación concurrentes
  const isAuthProcessingRef = useRef(false);
  // Ref para almacenar el estado de `loading` más reciente, para evitar cierres de `loading` obsoletos
  const latestLoadingState = useRef(true); // Initialize with true as App starts loading

  // Initialize auto-logout hook
  useAutoLogout();

  // Initialize Capacitor plugins for native platforms
  useEffect(() => {
    if (isNative) {
      const initializeCapacitor = async () => {
        try {
          if (isIOS) {
            await StatusBar.setStyle({ style: Style.Light });
          } else if (isAndroid) {
            await StatusBar.setBackgroundColor({ color: '#1e40af' });
          }

          await SplashScreen.hide();

          CapApp.addListener('appStateChange', ({ isActive }) => {
            console.log('App state changed. Is active:', isActive);
          });

          CapApp.addListener('backButton', ({ canGoBack }) => {
            if (!canGoBack) {
              CapApp.exitApp();
            } else {
              window.history.back();
            }
          });
        } catch (error) {
          console.error('Error initializing Capacitor:', error);
        }
      };

      initializeCapacitor();
    }
  }, [isNative, isIOS, isAndroid]);

  // Initialize analytics debug mode early
  useEffect(() => {
    analyticsService.initializeDebugMode();
  }, []);

  // Log the loading state during renders
  console.log('App.tsx render: current loading state is', loading, 'user is', user ? 'logged in' : 'not logged in');

  // Actualiza el ref del estado de carga en cada render
  useEffect(() => {
    latestLoadingState.current = loading;
  }, [loading]);

  // Auth state listener
  useEffect(() => {
    console.log('App.tsx useEffect: Setting up auth state listener');
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log(`Auth state change detected: Event -> ${event}, Session -> ${session ? 'exists' : 'null'}. isAuthProcessingRef.current: ${isAuthProcessingRef.current}`);
      
      // Si un ciclo de procesamiento de autenticación ya está en curso, omite este evento
      if (isAuthProcessingRef.current) {
        console.log('Auth state change: Already processing an auth event, skipping this one.');
        return;
      }

      isAuthProcessingRef.current = true; // Establece la bandera a true
      console.log('Auth state change: Setting isAuthProcessingRef to true.');

      // Establece loading a true solo si no está ya en true para evitar re-renders innecesarios
      // Usa latestLoadingState.current para obtener el valor más reciente
      if (!latestLoadingState.current) {
        console.log('Auth state change: Setting loading to true for event processing.');
        setLoading(true);
      } else {
        console.log('Auth state change: Loading is already true, skipping setLoading(true).');
      }

      try {
        if (session?.user) {
          console.log('Auth state change: User session found.');
          const userData: User = {
            uid: session.user.id,
            email: session.user.email || 'Usuario',
            displayName: session.user.user_metadata?.display_name || session.user.email?.split('@')[0] || 'Usuario'
          };
          
          console.log('Auth state change: Setting user data:', userData);
          setUser(userData);
          
          // Initialize user tokens and analytics in background (non-blocking)
          (async () => {
            try {
              console.log('Auth state change: Starting background initialization for user:', session.user.id);
              
              // Set user ID for analytics
              console.log('Auth state change: Setting analytics user ID.');
              await analyticsService.setUserId(session.user.id);
              
              // Set user properties for analytics
              console.log('Auth state change: Setting analytics user properties.');
              await analyticsService.setUserProperties({
                user_email_domain: session.user.email ? session.user.email.split('@')[1] : 'unknown',
                has_display_name: session.user.user_metadata?.display_name ? 'true' : 'false'
              });
              
              // Ensure user has initial tokens
              console.log('Auth state change: Initializing user tokens in background.');
              await initializeUserTokens(session.user);
              console.log('Auth state change: Background token initialization completed successfully.');
            } catch (backgroundError) {
              console.error('Error during background initialization (non-blocking):', backgroundError);
              // Background initialization errors don't affect the user's login state
              // The TokenStatus component will handle showing the error or low token state
            }
          })();
        } else {
          console.log('Auth state change: No user session found, setting user to null.');
          setUser(null);
        }
      } catch (error) {
        console.error("Error durante el cambio de estado de autenticación o la inicialización de tokens:", error);
        console.log('Auth state change: Error occurred, ensuring user state is null if no session.');
        // Only clear user state if there was no session to begin with
        if (!session?.user) {
          setUser(null);
        }
      } finally {
        console.log('Auth state change: Listener processing complete. Setting loading to false.');
        setLoading(false);
        isAuthProcessingRef.current = false; // Reinicia la bandera
        console.log('Auth state change: Reset isAuthProcessingRef to false.');
      }
    });

    // Initial check for session on component mount
    console.log('App.tsx useEffect: Performing initial session check');
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      console.log(`Initial session check: Session -> ${session ? 'exists' : 'null'}. isAuthProcessingRef.current: ${isAuthProcessingRef.current}`);
      
      // Previene la re-entrada si el listener de cambio de estado de auth ya lo capturó
      if (isAuthProcessingRef.current) {
        console.log('Initial session check: Auth processing already underway, skipping.');
        return;
      }

      isAuthProcessingRef.current = true; // Establece la bandera a true
      console.log('Initial session check: Setting isAuthProcessingRef to true.');

      try {
        if (session?.user) {
          console.log('Initial session check: User found, setting user data and initializing tokens.');
          const userData: User = {
            uid: session.user.id,
            email: session.user.email || 'Usuario',
            displayName: session.user.user_metadata?.display_name || session.user.email?.split('@')[0] || 'Usuario'
          };
          setUser(userData);
          
          // Initialize tokens and analytics in background (non-blocking)
          (async () => {
            try {
              console.log('Initial session check: Starting background initialization for user:', session.user.id);
              await analyticsService.setUserId(session.user.id);
              await analyticsService.setUserProperties({
                user_email_domain: session.user.email ? session.user.email.split('@')[1] : 'unknown',
                has_display_name: session.user.user_metadata?.display_name ? 'true' : 'false'
              });
              await initializeUserTokens(session.user);
              console.log('Initial session check: Background token initialization completed successfully.');
            } catch (backgroundError) {
              console.error('Error during background initialization (non-blocking):', backgroundError);
              // Background initialization errors don't affect the user's login state
            }
          })();
        } else {
          console.log('Initial session check: No session found, user is null.');
          setUser(null);
        }
      } catch (error) {
        console.error('Error during initial session check:', error);
        console.log('Initial session check: Error occurred, but keeping user state if session exists.');
        // Only clear user state if there was no session to begin with
        if (!session?.user) {
          setUser(null);
        }
      } finally {
        console.log('Initial session check: Setting loading to false in finally block.');
        setLoading(false);
        isAuthProcessingRef.current = false; // Reinicia la bandera
        console.log('Initial session check: Reset isAuthProcessingRef to false.');
      }
    });

    return () => {
      console.log('App.tsx useEffect cleanup: Unsubscribing from auth state listener.');
      subscription.unsubscribe();
    };
  }, []);

  // Listen for hash changes to show terms and conditions or privacy policy
  useEffect(() => {
    const handleHashChange = () => {
      setShowTerms(window.location.hash === '#terminos-y-condiciones');
      setShowPrivacy(window.location.hash === '#aviso-de-privacidad');
    };

    // Check current hash
    handleHashChange();

    // Add listener for future changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Handle PWA install prompt display
  useEffect(() => {
    if (!isNative && canInstall && !isInstalled && !wasDismissed && !loading) {
      setShowInstallPrompt(true);
    } else {
      setShowInstallPrompt(false);
    }
  }, [isNative, canInstall, isInstalled, wasDismissed, loading]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <Truck size={64} className="mx-auto text-yellow-500 animate-pulse" />
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // If showing terms and conditions
  if (showTerms) {
    return <TermsAndConditions onClose={() => {
      window.history.back(); // Go back to previous page
      setShowTerms(false);
    }} />;
  }

  // If showing privacy policy
  if (showPrivacy) {
    return <PrivacyPolicy onClose={() => {
      window.history.back(); // Go back to previous page
      setShowPrivacy(false);
    }} />;
  }

  return (
    <CurrencyProvider>
      <Router>
        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        {showInstallPrompt && (
          <InstallPWAPrompt
            onInstall={promptInstall}
            onDismiss={() => {
              dismissPrompt();
              setShowInstallPrompt(false);
            }}
          />
        )}
        <Routes>
          <Route
            path="/"
            element={user ? <Dashboard user={user} onUserLoggedOut={handleUserLoggedOut} /> : <AuthForm onSuccess={() => {}} />}
          />
          <Route
            path="/guia-formulario"
            element={user ? <FormGuidePage /> : <Navigate to="/" />}
          />
          <Route
            path="/informacion-adicional"
            element={user ? <AdditionalInfoPage /> : <Navigate to="/" />}
          />
          <Route
            path="/reset-password"
            element={<ResetPasswordPage />}
          />
          <Route
            path="/success"
            element={user ? <SuccessPage /> : <Navigate to="/" />}
          />
          <Route
            path="/cancel"
            element={user ? <CancelPage /> : <Navigate to="/" />}
          />
        </Routes>
      </Router>
    </CurrencyProvider>
  );
}

export default App;